import { supabase } from './supabaseClient';
import { getCurrentCopy } from '../i18n/current';
import { projectInviteService } from './projectInviteService';
import type { ProjectMember, ProjectMemberWithUser, ProjectMemberRole } from '../types';
import type { PostgrestError } from '@supabase/supabase-js';

// Listar membros do projeto
export async function getProjectMembers(projectId: string): Promise<{ data: ProjectMemberWithUser[] | null; error: PostgrestError | null }> {
  const { data, error } = await supabase
    .from('project_members')
    .select(`
      *,
      user:profiles!fk_project_members_user_profile(id, email, full_name, avatar_url)
    `)
    .eq('project_id', projectId)
    .order('role', { ascending: true });

  return { data: data as ProjectMemberWithUser[] | null, error };
}

// Convidar membro para o projeto
// Se o email existe → adiciona direto como project_member
// Se não existe → cria project_invite e envia email
export async function inviteMember(
  projectId: string,
  email: string,
  role: ProjectMemberRole,
  invitedBy: string
): Promise<{ data?: ProjectMember | null; invited?: boolean; emailSent?: boolean; error?: { message: string } | PostgrestError | null }> {
  // Buscar o usuário pelo email (via SECURITY DEFINER para bypassar RLS)
  const { data: userId } = await supabase.rpc('lookup_profile_id_by_email', { p_email: email });

  if (userId) {
    // Usuário existe → adicionar direto
    const { data: existing } = await supabase
      .from('project_members')
      .select('id')
      .eq('project_id', projectId)
      .eq('user_id', userId)
      .single();

    if (existing) {
      return { error: { message: getCurrentCopy().common.serviceErrors.alreadyMember } };
    }

    const { data, error } = await supabase
      .from('project_members')
      .insert({
        project_id: projectId,
        user_id: userId,
        role: role,
        invited_by: invitedBy,
        accepted_at: new Date().toISOString()
      })
      .select()
      .single();

    return { data: data as ProjectMember | null, error };
  }

  // Usuário NÃO existe → criar convite por email
  try {
    const { inviteId } = await projectInviteService.invite(projectId, email, role);
    const emailResult = await projectInviteService.sendInviteEmail(inviteId);
    if (!emailResult.success) {
      console.error('[inviteMember] Convite criado mas email não foi entregue para', email, '— motivo:', emailResult.reason || emailResult.error);
    }
    return { invited: true, emailSent: emailResult.success };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Erro ao criar convite';
    return { error: { message } };
  }
}

// Alterar role de um membro
export async function updateMemberRole(projectId: string, userId: string, newRole: ProjectMemberRole): Promise<{ data: ProjectMember | null; error: PostgrestError | null }> {
  const { data, error } = await supabase
    .from('project_members')
    .update({ role: newRole })
    .eq('project_id', projectId)
    .eq('user_id', userId)
    .select()
    .single();

  return { data: data as ProjectMember | null, error };
}

// Remover membro do projeto
export async function removeMember(projectId: string, userId: string): Promise<{ error: PostgrestError | null }> {
  const { error } = await supabase
    .from('project_members')
    .delete()
    .eq('project_id', projectId)
    .eq('user_id', userId);

  return { error };
}

// Verificar se usuário tem acesso ao projeto
export async function checkProjectAccess(projectId: string, userId: string): Promise<{ hasAccess: boolean; role: ProjectMemberRole | undefined; error: PostgrestError | null }> {
  const { data, error } = await supabase
    .from('project_members')
    .select('role')
    .eq('project_id', projectId)
    .eq('user_id', userId)
    .single();

  return { hasAccess: !!data, role: (data as { role: ProjectMemberRole } | null)?.role, error };
}

// Transferir ownership
export async function transferOwnership(projectId: string, currentOwnerId: string, newOwnerId: string): Promise<{ error: PostgrestError | null }> {
  // Transação: rebaixar owner atual e promover novo owner
  const { error: error1 } = await supabase
    .from('project_members')
    .update({ role: 'admin' })
    .eq('project_id', projectId)
    .eq('user_id', currentOwnerId);

  if (error1) return { error: error1 };

  const { error: error2 } = await supabase
    .from('project_members')
    .update({ role: 'owner' })
    .eq('project_id', projectId)
    .eq('user_id', newOwnerId);

  return { error: error2 };
}

// Adicionar owner ao criar projeto
export async function addProjectOwner(projectId: string, userId: string): Promise<{ data: ProjectMember | null; error: PostgrestError | null }> {
  const { data, error } = await supabase
    .from('project_members')
    .insert({
      project_id: projectId,
      user_id: userId,
      role: 'owner',
      accepted_at: new Date().toISOString()
    })
    .select()
    .single();

  return { data: data as ProjectMember | null, error };
}
